import { useState, useCallback } from 'react';
import { useUploadFirmware } from './useFirmwareMutations';
import { readVersionNumberFromFile } from '../components/firmware/versionNumberFromFile';

interface UseFirmwareFileParams {
  modelId: number;
  // Seeds hasUploadedFile so the draft flow knows a file already exists on the server.
  initiallyUploaded?: boolean;
  // Called after a file is selected with the number read from its header and its name.
  onFileDerived: (numberPart: string, fileName: string) => void;
  // Called when the selection is cleared, so the caller can reset derived version state.
  onFileCleared?: () => void;
}

export interface UseFirmwareFileResult {
  file: File | null;
  hasUploadedFile: boolean;
  showFileUpload: boolean;
  isUploading: boolean;
  selectFile: (file: File | null) => Promise<void>;
  setShowFileUpload: (show: boolean) => void;
  uploadTo: (firmwareId: string) => Promise<void>;
  clearFile: () => void;
}

// Owns the firmware file lifecycle: selection, header number derivation and upload. Selecting
// a file reports its derived number and name back to the caller so the version editor can pick
// them up; uploadTo pushes the pending file to a firmware and marks it uploaded.
export function useFirmwareFile({
  modelId,
  initiallyUploaded = false,
  onFileDerived,
  onFileCleared,
}: UseFirmwareFileParams): UseFirmwareFileResult {
  const [file, setFile] = useState<File | null>(null);
  const [hasUploadedFile, setHasUploadedFile] = useState(initiallyUploaded);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const uploadFirmware = useUploadFirmware();

  // Stores the picked file and reads the version number out of its header, reporting both the
  // number and the file name to the caller; clearing the selection reports the reset instead.
  const selectFile = useCallback(async (selected: File | null) => {
    setFile(selected);
    if (!selected) {
      onFileCleared?.();
      return;
    }
    const derivedNumber = await readVersionNumberFromFile(selected);
    onFileDerived(derivedNumber, selected.name);
  }, [onFileDerived, onFileCleared]);

  // Uploads the pending file to the given firmware, then marks it uploaded and clears the picker.
  const uploadTo = useCallback(async (firmwareId: string) => {
    if (!file) return;
    await uploadFirmware.mutateAsync({ modelId, firmwareId, file });
    setHasUploadedFile(true);
    setFile(null);
    setShowFileUpload(false);
  }, [file, modelId, uploadFirmware]);

  const clearFile = useCallback(() => setFile(null), []);

  return {
    file,
    hasUploadedFile,
    showFileUpload,
    isUploading: uploadFirmware.isPending,
    selectFile,
    setShowFileUpload,
    uploadTo,
    clearFile,
  };
}
